import axios from 'axios'
import toast from 'react-hot-toast'

// A 401 here means bad credentials, not an expired session. Anchored on the
// /auth/ segment so a path like /api/audit/logins doesn't match.
export function isCredentialCall(url) {
  return /\/auth\/(login|register|forgot-password|reset-password)/.test(url || '')
}

// Auth-init calls and health pings handle their own 401s; credential calls
// surface the error on the page that made them.
export function isSilent401(url) {
  const u = url || ''
  return /\/auth\/(me|refresh)/.test(u) || /\/health(\?|$)/.test(u) || isCredentialCall(u)
}

// The landing page's reads each fall back to hardcoded content, so a 5xx toast
// would announce a failure the page already handled. GET only — the same paths
// serve admin POSTs whose errors must still surface.
export function isBestEffortLandingRead(url, method) {
  return (
    (method || 'get').toLowerCase() === 'get' &&
    /\/api\/public\/(landing-content|metrics|images|team)(\/|\?|$)/.test(url || '')
  )
}

export function isSilent5xx(url, method) {
  const u = url || ''
  return /\/health(\?|$)/.test(u) || isBestEffortLandingRead(u, method) || isCredentialCall(u)
}

let redirectingToLogin = false

// The toast has to fire before the navigation: window.location unloads the
// document synchronously, so anything queued after it never gets a render tick.
// Latched because several requests can 401 at once.
export function handleSessionExpired() {
  // Ahead of the early-returns — the token is dead either way.
  localStorage.removeItem('iewrs_token')

  if (window.location.pathname === '/login') return
  if (redirectingToLogin) return
  redirectingToLogin = true

  toast.error('Session expired. Please login again.', { id: 'session-expired' })

  setTimeout(() => {
    window.location.href = '/login'
    setTimeout(() => { redirectingToLogin = false }, 2000)
  }, 400)
}

// Create axios instance with base configuration.
// Default empty string = same-origin: in dev, Vite's proxy (vite.config.js) forwards
// each /api/<svc>/* to the right backend port; in prod, nginx.conf does the same via
// its /api/* location blocks. Override via VITE_API_BASE_URL only if you're hosting
// the API on a different origin (e.g. https://api.example.com).
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('iewrs_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  async (error) => {
    const { response } = error

    if (response?.status === 401) {
      // Don't force-redirect on auth init calls or health pings — AuthContext /
      // landing page handle those gracefully. A 401 from /auth/login means bad
      // credentials, not an expired session — that page shows its own error inline.
      const url = error.config?.url || ''
      if (!isSilent401(url)) {
        handleSessionExpired()
      }
    } else if (response?.status === 403) {
      // Stable ids: a page firing several requests at once would otherwise stack
      // one identical toast per failure.
      toast.error('Access denied. Insufficient permissions.', { id: 'forbidden' })
    } else if (response?.status === 429) {
      toast.error('Too many requests. Please try again later.', { id: 'rate-limited' })
    } else if (response?.status >= 500) {
      // Silent 5xx for health pings, the landing page's best-effort reads,
      // and credential calls — see isSilent5xx() above for the reasoning
      // behind each case.
      const url = error.config?.url || ''
      const method = error.config?.method || 'get'
      if (!isSilent5xx(url, method)) {
        toast.error('Server error. Please try again later.', { id: 'server-error' })
      }
    }

    return Promise.reject(error)
  }
)

// Auth API endpoints
export const authAPI = {
  login: (email, password) =>
    api.post('/api/auth/login', { email, password }),

  register: (userData) =>
    api.post('/api/auth/register', userData),

  logout: () =>
    api.post('/api/auth/logout'),

  refreshToken: () =>
    api.post('/api/auth/refresh'),

  getProfile: () =>
    api.get('/api/auth/me'),

  // NOTE: there is no `PUT /api/auth/profile` route on the auth service —
  // an earlier `updateProfile()` method pointed at that dead URL and was
  // never called from anywhere. The real self-service profile update is
  // `PUT /api/auth/me/profile`, below as updateMyProfile(). Use that.

  changePassword: (currentPassword, newPassword) =>
    api.put('/api/auth/change-password', { currentPassword, newPassword }),

  requestPasswordReset: (email) =>
    api.post('/api/auth/forgot-password', { email }),

  resetPassword: (token, newPassword) =>
    api.post('/api/auth/reset-password', { token, newPassword }),

  getUsers: (params = {}) =>
    api.get('/api/auth/users', { params }),

  updateUserRole: (userId, role) =>
    api.put(`/api/auth/users/${userId}/role`, { role }),

  deleteUser: (userId) =>
    api.delete(`/api/auth/users/${userId}`),

  // Rich profile (user-driven team)
  getMyProfile: () =>
    api.get('/api/auth/me/profile'),

  updateMyProfile: (patch) =>
    api.put('/api/auth/me/profile', patch),
}

// Prediction Engine API endpoints (Python service, :3004 → /api/predictions)
// All endpoints write/read against predictions_schema.{prediction_runs,
// prediction_results,prediction_history}. See
// docs/ml-model-prediction-engine-plan.md for the full contract.
export const predictionAPI = {
  // Trigger a run. Body: { disease, sectors[], months_ahead, target_date?,
  //                       district?, run_type? }
  // Returns: { run_id, status: 'pending', queued_sectors, message }
  runPrediction: (body) =>
    api.post('/api/predictions/run', body, { timeout: 30000 }),

  // Run status + result count. Use to poll after runPrediction returns.
  getRun: (runId) =>
    api.get(`/api/predictions/runs/${runId}`),

  // List runs (filterable). Used by future "Run History" surfaces.
  listRuns: (params = {}) =>
    api.get('/api/predictions/runs', { params }),

  // Filtered prediction_results — call with run_id to fetch the rows
  // produced by a specific run, or with date/sector/disease filters for
  // ad-hoc analytics.
  getResults: (params = {}) =>
    api.get('/api/predictions/results', { params }),

  // Disk ∩ DB registry view. Shows every joblib in storage plus its
  // registry status, sector_id, features, has_scaler.
  getAvailableModels: () =>
    api.get('/api/predictions/models/available'),

  // Pre-flight check: can this sector's model actually run against the
  // current DB? Returns { runnable, metrics_required, metrics_missing,
  // unknown_variables, reason }.
  checkReachable: (sector, params = {}) =>
    api.get(`/api/predictions/models/${encodeURIComponent(sector)}/reachable`, { params }),

  // Walk the storage volume and register everything in models_schema.
  // Idempotent. Used by ops after a bulk model drop.
  bulkImportModels: () =>
    api.post('/api/predictions/models/bulk-import'),
}

// Model Intelligence API endpoints
export const modelAPI = {
  uploadModel: (formData, onUploadProgress) =>
    api.post('/api/models/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    }),

  getModels: (params = {}) =>
    api.get('/api/models', { params }),

  // Alias used by ModelsPage
  listModels: (params = {}) =>
    api.get('/api/models', { params }),

  getModel: (modelId) =>
    api.get(`/api/models/${modelId}`),

  deleteModel: (modelId) =>
    api.delete(`/api/models/${modelId}`),

  extractParameters: (modelId) =>
    api.post(`/api/models/${modelId}/extract-parameters`),

  generatePreview: (modelId) =>
    api.post(`/api/models/${modelId}/generate-preview`),

  generateSchema: (modelId) =>
    api.post(`/api/models/${modelId}/generate-schema`),

  getModelAnalysis: (modelId) =>
    api.get(`/api/models/${modelId}/analysis`),

  updateModelMetadata: (modelId, metadata) =>
    api.put(`/api/models/${modelId}/metadata`, metadata),

  getModelFormats: () =>
    api.get('/api/models/supported-formats'),

  validateModel: (formData) =>
    api.post('/api/models/validate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // --- Variable mapping & prediction endpoints (mock fallback) ---

  getAvailableMetrics: async () => {
    try {
      const response = await api.get('/api/models/data/available-metrics')
      return response
    } catch {
      return { metrics: [] }
    }
  },

  // Server is the source of truth. localStorage is only an OFFLINE fallback
  // (no HTTP response at all). A server that responds with an error — 403
  // "not your model", 404, 5xx — must NOT be masked as a local read, so we
  // rethrow those.
  getModelMappings: async (modelId) => {
    try {
      const response = await api.get(`/api/models/${modelId}/mappings`)
      return response
    } catch (error) {
      if (error?.response) throw error
      const stored = localStorage.getItem(`iewrs_mappings_${modelId}`)
      if (stored) {
        try {
          return JSON.parse(stored)
        } catch { /* ignore */ }
      }
      return { mappings: [] }
    }
  },

  saveModelMappings: async (modelId, mappings) => {
    try {
      const response = await api.post(`/api/models/${modelId}/mappings`, { mappings })
      return response
    } catch (error) {
      // Real server rejection (auth/validation/server error) — surface it so
      // the UI doesn't lie about a save that didn't happen.
      if (error?.response) throw error
      // Offline only: cache locally so unsaved work isn't lost, and say so.
      localStorage.setItem(`iewrs_mappings_${modelId}`, JSON.stringify({
        mappings,
        savedAt: new Date().toISOString(),
        modelId,
        pendingSync: true,
      }))
      return { message: 'Saved locally (offline) — will not sync until you reconnect', count: mappings.length, offline: true }
    }
  },

  getDataAvailability: async (modelId) => {
    try {
      const response = await api.get(`/api/models/${modelId}/data-availability`)
      return response
    } catch {
      return { ready_count: 0, partial_count: 0, missing_count: 0, total_features: 0 }
    }
  },

  runPrediction: async (modelId, params) => {
    return await api.post(`/api/models/${modelId}/predict`, params)
  },
}

// Notebook management.
// Backend lives in services/model-intelligence/routes/notebooks.js.
// One row per upload, versioned per disease, with a partial unique index
// guaranteeing exactly one active notebook per disease at any time.
export const notebooksAPI = {
  // multipart/form-data with field name "notebook" (the .ipynb) and
  // optional "name" (display string). Returns the parsed contract, version,
  // and warnings.
  upload: (file, opts = {}) => {
    const fd = new FormData()
    if (file) fd.append('notebook', file)
    if (opts.name) fd.append('name', opts.name)
    // Phase B overrides — operator-confirmed metadata
    if (opts.override_disease)           fd.append('override_disease', opts.override_disease)
    if (opts.override_climate_variables) fd.append('override_climate_variables', JSON.stringify(opts.override_climate_variables))
    if (opts.override_sectors !== undefined) {
      fd.append('override_sectors',
        typeof opts.override_sectors === 'string' ? opts.override_sectors : JSON.stringify(opts.override_sectors))
    }
    if (opts.override_start_date) fd.append('override_start_date', opts.override_start_date)
    if (opts.override_end_date)   fd.append('override_end_date',   opts.override_end_date)
    if (opts.edited_from_original) fd.append('edited_from_original', 'true')
    if (opts.notebook_json) fd.append('notebook_json', JSON.stringify(opts.notebook_json))
    return api.post('/api/notebooks', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    })
  },

  // Phase B "smart-detect" — analyze without storing
  analyze: (file, opts = {}) => {
    const fd = new FormData()
    if (file) fd.append('notebook', file)
    if (opts.name) fd.append('name', opts.name)
    if (opts.notebook_json) fd.append('notebook_json', JSON.stringify(opts.notebook_json))
    return api.post('/api/notebooks/analyze', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    })
  },

  // Phase C "path conversion" — AST-rewrite hardcoded local CSV reads to
  // in-system iewrs_data.dataset_csv(...) calls. Stateless like analyze:
  // returns { ok, notebook (rewritten .ipynb JSON), report }. create_models
  // gated server-side. Accepts a File OR an inline (possibly edited) JSON.
  convert: (fileOrJson, opts = {}) => {
    const fd = new FormData()
    if (fileOrJson instanceof File || fileOrJson instanceof Blob) {
      fd.append('notebook', fileOrJson)
    } else if (fileOrJson && typeof fileOrJson === 'object') {
      fd.append('notebook_json', JSON.stringify(fileOrJson))
    }
    if (opts.name) fd.append('name', opts.name)
    // Opt-in advisory smoke-run gate: server executes the converted notebook on
    // one sector and returns a `smoke` result (never blocks). No-op without a
    // live runtime (returns reason:'no_runtime' = "not checked"). Longer timeout
    // since a real smoke run adds a papermill execution.
    if (opts.smoke) {
      fd.append('smoke', 'true')
      return api.post('/api/notebooks/convert', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 150000,
      })
    }
    return api.post('/api/notebooks/convert', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 45000,
    })
  },

  // Filter by ?disease=... and/or ?activeOnly=true
  // Also the "Models" entity source for the ⌘K global search
  // (hooks/useGlobalSearch.js's useGlobalSearchEntities) — called with no
  // params (every notebook, not just active-per-disease) so a search for an
  // older/inactive version still finds it.
  list: (params = {}) =>
    api.get('/api/notebooks', { params }),

  get: (notebookId, opts = {}) =>
    api.get(`/api/notebooks/${notebookId}`, { params: opts }),

  // Activate atomically deactivates the currently-active notebook for the
  // same disease (stamping deactivated_at) and activates the target.
  activate: (notebookId) =>
    api.post(`/api/notebooks/${notebookId}/activate`),

  deactivate: (notebookId) =>
    api.post(`/api/notebooks/${notebookId}/deactivate`),

  // Run a notebook.
  // Pass requireActive=false to run a non-active (historical) version
  // — useful for re-running old code against new data for comparison.
  run: (notebookId, overrides = {}, { requireActive = true } = {}) =>
    api.post(`/api/notebooks/${notebookId}/run`, {
      overrides,
      require_active: requireActive,
    }),

  listRuns: (params = {}) =>
    api.get('/api/notebooks/runs', { params }),

  getRun: (runId) =>
    api.get(`/api/notebooks/runs/${runId}`),

  getRunMetrics: (runId) =>
    api.get(`/api/notebooks/runs/${runId}/metrics`),

  getRunLog: (runId) =>
    api.get(`/api/notebooks/runs/${runId}/log`),

  listRunOutputs: (runId) =>
    api.get(`/api/notebooks/runs/${runId}/outputs`),

  // PNGs need an auth header so we can't use a bare <img src=...>. Frontend
  // fetches the blob, then creates an object URL. Returns a Promise<Blob>.
  fetchRunOutputBlob: (runId, filename) =>
    api.get(`/api/notebooks/runs/${runId}/outputs/${encodeURIComponent(filename)}`, {
      responseType: 'blob',
    }),

  cancelRun: (runId) =>
    api.post(`/api/notebooks/runs/${runId}/cancel`),

  // Server-side preview of an Excel output — returns first 20 rows ×
  // first 3 sheets parsed into JSON.
  previewRunXlsx: (runId, file) =>
    api.get(`/api/notebooks/runs/${runId}/preview/xlsx`, { params: { file } }),

  // Server-side preview of an .ipynb output — returns cells with source +
  // plain-text outputs.
  previewRunIpynb: (runId, file = 'executed.ipynb') =>
    api.get(`/api/notebooks/runs/${runId}/preview/ipynb`, { params: { file } }),

  // Run-worker pool config (GUI-configurable). GET/PUT gated manage_settings;
  // cleanup gated manage_models. See runController.js run-settings section.
  getRunSettings: () =>
    api.get('/api/notebooks/run-settings'),
  updateRunSettings: (patch) =>
    api.put('/api/notebooks/run-settings', patch),
  // body { status?: 'failed'|'cancelled'|'timed_out', older_than_days?: number|null }
  cleanupRuns: (body = {}) =>
    api.post('/api/notebooks/runs/cleanup', body),
}

// Sector anomaly alerts.
export const alertsAPI = {
  // Also the "Active alerts" entity source for the ⌘K global search
  // (hooks/useGlobalSearch.js's useGlobalSearchEntities) — called with
  // { status: 'active', limit: 100 }; gated client-side on the caller
  // holding view_predictions (this route's own requirePermission), so the
  // hook never fires this and eats a 403 for a user who can't see alerts.
  list: (params = {}) =>
    api.get('/api/alerts', { params }),
  // Grouped view: anomalies rolled up to one row per (sector × metric × source).
  // Returns the worst alert in each group + counts by severity. Used by the
  // dashboard's Layer-3 "grouped rollup" view.
  listGroups: (params = {}) =>
    api.get('/api/alerts/groups', { params }),
  // Layer 2: district-level aggregation for the Rwanda choropleth.
  byDistrict: (params = {}) =>
    api.get('/api/alerts/by-district', { params }),
  // Sector-level severity for the 416-sector Forecast Severity Map. params:
  // { month: 'YYYY-MM', disease?, status? } — see alertsController.getAlertsBySector.
  bySector: (params = {}) =>
    api.get('/api/alerts/by-sector', { params }),
  // Same params as bySector, as an .xlsx workbook for offline audit.
  exportBySector: (params = {}) =>
    api.get('/api/alerts/by-sector/export', { params, responseType: 'blob' }),
  // Layer 2: time-bucketed alert counts for the weekly trend chart.
  trend: (params = {}) =>
    api.get('/api/alerts/trend', { params }),
  stats: (params = {}) =>
    api.get('/api/alerts/stats', { params }),
  acknowledge: (anomalyId, note) =>
    api.post(`/api/alerts/${anomalyId}/acknowledge`, { note }),
  recompute: (opts = {}) =>
    api.post('/api/alerts/recompute', opts),
  // Drill-in modal payload: alert + 24mo measurements + 12 monthly thresholds.
  context: (anomalyId, months = 24) =>
    api.get(`/api/alerts/${anomalyId}/context`, { params: { months } }),
  // Drill-in for a Blaise operational-overlay row — no anomaly_id to key off,
  // so keyed on (sector_id, disease, month) instead. See
  // alertsController.getOperationalAlertContext.
  operationalContext: ({ sectorId, disease, month }, months = 24) =>
    api.get('/api/alerts/operational-context', { params: { sector_id: sectorId, disease, month, months } }),

  // Public Health SOP Interventions & Actions
  recordActions: (payload) =>
    api.post('/api/alerts/actions', payload),
  listActions: (params = {}) =>
    api.get('/api/alerts/actions', { params }),
  updateAction: (interventionId, patch) =>
    api.patch(`/api/alerts/actions/${interventionId}`, patch),

  // Detector config admin endpoints (admin only)
  listConfigProfiles:    () => api.get('/api/alerts/config'),
  // Clone-only create — server copies every tunable off source_profile_id
  // (incl. moh_thresholds) into a new inactive row named profile_name.
  createConfigProfile:   (sourceProfileId, profileName) =>
    api.post('/api/alerts/config', { source_profile_id: sourceProfileId, profile_name: profileName }),
  updateConfigProfile:   (profileId, patch) => api.patch(`/api/alerts/config/${profileId}`, patch),
  deleteConfigProfile:   (profileId) => api.delete(`/api/alerts/config/${profileId}`),
  activateConfigProfile: (profileId) => api.post(`/api/alerts/config/${profileId}/activate`),
}

// Ingest Review — governance gate on pulled DHIS2/HMIS/NHIC batches before
// they drive the detector. batchId == dataset_version id; rowRef is opaque,
// backend-assigned (sector_id|metric|period), passed back verbatim on flag.
export const ingestAPI = {
  listBatches: (params = {}) =>
    api.get('/api/ingest/batches', { params }),
  // Distinct source keys actually present — backs the filter dropdown so a
  // new integration shows up with zero frontend change.
  listSources: () =>
    api.get('/api/ingest/sources'),
  // Set the auto-approve policy for every data_source under one source key
  // (e.g. 'dhis2'). policy ∈ manual|auto-clean|auto-all — server-validated,
  // gated on review_ingest. Governs whether a pull's batches self-approve.
  setSourcePolicy: (value, policy) =>
    api.patch(`/api/ingest/sources/${value}/policy`, { policy }),
  getBatch: (batchId) =>
    api.get(`/api/ingest/batches/${batchId}`),
  // Approve triggers detector recompute server-side, exactly once
  // (recompute_triggered guard) — this is the correctness-critical leg.
  approve: (batchId, note) =>
    api.post(`/api/ingest/batches/${batchId}/approve`, { note }),
  hold: (batchId, reason) =>
    api.post(`/api/ingest/batches/${batchId}/hold`, { reason }),
  flagRow: (batchId, rowRef, body) =>
    api.post(`/api/ingest/batches/${batchId}/rows/${rowRef}/flag`, body),
  // Withhold an already-approved batch from the detector, and put it back.
  // Reversible governance, not deletion: the server writes only
  // ingest_schema.batch_reviews + batch_rollback_events, never a measurement.
  // `reason` is mandatory both ends — blank is a 400. Both trigger a recompute
  // AFTER the state flip commits, so a 5xx here can still mean the flip took
  // (see rollbackLogic.describeTransitionError).
  rollback: (batchId, reason) =>
    api.post(`/api/ingest/batches/${batchId}/rollback`, { reason }),
  restore: (batchId, reason) =>
    api.post(`/api/ingest/batches/${batchId}/restore`, { reason }),
}

// Dynamic Schema API endpoints — the dynamic-schema service mounts five
// SEPARATE top-level prefixes (/api/schema, /api/tables, /api/migrations,
// /api/versioning, /api/data), not one /api/schema/* namespace. See
// services/dynamic-schema/index.js and routes/{schema,tables,migrations,
// versioning}.js for the source of truth.
// Server-side visuals + dashboards (Stage 2 of the localStorage → API move —
// see frontend/src/hooks/useSavedVisuals.js for the shared data-access layer
// that consumes these). Backed by services/model-intelligence's
// visualsController.js / dashboardsController.js, same origin + auth
// pattern as notebooksAPI / alertsAPI above (mounted on the model-
// intelligence service, port 3002 in dev).
//
// NOTE: this endpoint set ships on branch feat/visuals-api (PR #28), not yet
// merged as of this track. The calls below are correct against that
// branch's contract but will 404 against main until #28 lands.
export const visualsAPI = {
  // mine=true restricts to the caller's own visuals; omit/false also
  // includes everyone's is_public visuals.
  list: (params = {}) =>
    api.get('/api/visuals', { params }),

  get: (visualId) =>
    api.get(`/api/visuals/${visualId}`),

  create: (payload) =>
    api.post('/api/visuals', payload),

  update: (visualId, patch) =>
    api.put(`/api/visuals/${visualId}`, patch),

  remove: (visualId) =>
    api.delete(`/api/visuals/${visualId}`),
}

export const dashboardsAPI = {
  // Lazily creates the caller's dashboard server-side on first call.
  // Response shape: { dashboard: { dashboard_id, ..., visuals: [...] } }
  getDefault: () =>
    api.get('/api/dashboards/default'),

  // Idempotent — pinning an already-pinned visual updates its
  // position/size instead of erroring.
  pinVisual: (dashboardId, payload) =>
    api.post(`/api/dashboards/${dashboardId}/visuals`, payload),

  unpinVisual: (dashboardId, visualId) =>
    api.delete(`/api/dashboards/${dashboardId}/visuals/${visualId}`),

  // Whole-array atomic rearrange. Not currently called from the UI — the
  // dashboard doesn't support drag-and-drop reordering yet — but exposed
  // here so that feature doesn't need a second round of api.js wiring.
  saveLayout: (dashboardId, items) =>
    api.put(`/api/dashboards/${dashboardId}/layout`, { items }),
}

// Alert-notification settings admin endpoints (Track A/B). Every route is
// gated `manage_alert_notifications` server-side — see
// services/model-intelligence/routes/alertNotifications.js. test-send and
// dispatch-now are 501 stubs today (Track C/D fill them in); callers
// should handle a 501 gracefully rather than treat it as a hard failure.
export const alertNotificationsAPI = {
  getSettings:    () => api.get('/api/alerts/notifications/settings'),
  updateSettings: (patch) => api.put('/api/alerts/notifications/settings', patch),
  // secret semantics (see the controller docblock): key omitted from
  // `patch` -> leave the stored secret unchanged; "" -> clear it;
  // non-empty string -> encrypt + store it. Never send a plaintext
  // secret unless the admin actually typed a new one.
  putChannel: (channel, patch) => api.put(`/api/alerts/notifications/channels/${channel}`, patch),

  listRecipients:  (params = {}) => api.get('/api/alerts/notifications/recipients', { params }),
  createRecipient: (body) => api.post('/api/alerts/notifications/recipients', body),
  updateRecipient: (recipientId, patch) => api.patch(`/api/alerts/notifications/recipients/${recipientId}`, patch),
  deleteRecipient: (recipientId) => api.delete(`/api/alerts/notifications/recipients/${recipientId}`),

  listDeliveries: (params = {}) => api.get('/api/alerts/notifications/deliveries', { params }),

  // STUBS today — return 501 until Track C (dispatch) / Track D
  // (scheduler) land. Wired now so the buttons don't need touching later.
  testSend:    (body) => api.post('/api/alerts/notifications/test-send', body),
  dispatchNow: () => api.post('/api/alerts/notifications/dispatch-now'),

  // Multi-config channels (Track D) — a channel holds a LIST of named
  // configs (SendGrid and/or SMTP for email; failover by priority) instead
  // of the single row above. Same 3-case secret semantics as putChannel.
  listConfigs:  () => api.get('/api/alerts/notifications/configs'),
  createConfig: (body) => api.post('/api/alerts/notifications/configs', body),
  updateConfig: (id, patch) => api.put(`/api/alerts/notifications/configs/${id}`, patch),
  deleteConfig: (id) => api.delete(`/api/alerts/notifications/configs/${id}`),
  testConfig:   (id, body) => api.post(`/api/alerts/notifications/configs/${id}/test`, body),
}

// External Integrations Registry — admin CRUD + connection test for
// outbound integrations (DHIS2, SMTP, SMS gateways, generic REST APIs).
// Every route is gated `manage_integrations` server-side (super-admin
// only) — see the backend's routes/integrations.js. List/get responses
// are always MASKED (secret_last4 only, never ciphertext/plaintext); the
// 3-case secret semantics on create/update mirror alertNotificationsAPI's
// putChannel above (omit = unchanged, "" = clear, string = set).
// Super-admin audit log read API (services/auth/routes/audit.js), mounted on
// the auth service at /api/auth/audit. Gated view_audit_logs server-side.
export const auditAPI = {
  // params: action, user_id, resource_type, from, to, q, limit, offset
  listLogs: (params = {}) => api.get('/api/auth/audit/logs', { params }),
  // { actions: [...], resourceTypes: [...] } for the filter dropdowns
  listActions: () => api.get('/api/auth/audit/actions'),
}

export const integrationsAPI = {
  list: () => api.get('/api/integrations'),
  get: (id) => api.get(`/api/integrations/${id}`),
  create: (body) => api.post('/api/integrations', body),
  update: (id, body) => api.put(`/api/integrations/${id}`, body),
  remove: (id) => api.delete(`/api/integrations/${id}`),
  // No secret in the response — { ok, status_code, latency_ms, error }.
  test: (id) => api.post(`/api/integrations/${id}/test`),
}

// Integration-type catalog, mounted at /api/integration-types (separate
// mount, not a sub-path of /api/integrations). type_key is immutable after
// create — no rename endpoint.
export const integrationTypesAPI = {
  listIntegrationTypes: () => api.get('/api/integration-types'),
  getIntegrationType: (key) => api.get(`/api/integration-types/${key}`),
  createIntegrationType: (body) => api.post('/api/integration-types', body),
  updateIntegrationType: (key, patch) => api.put(`/api/integration-types/${key}`, patch),
  deleteIntegrationType: (key) => api.delete(`/api/integration-types/${key}`),
}

// DHIS2 management — org-unit + data-element mapping, on-demand pull/push,
// for a single `dhis2`-typed row in the Integrations Registry above. Every
// route is gated `manage_integrations` server-side (same gate as the
// registry) — see services/model-intelligence/routes/dhis2.js +
// docs/DHIS2.md. Credentials are resolved server-side and never returned.
//
// There is NO GET listing endpoint for the data-element mapping table today
// (routes/dhis2.js only exposes the two POST upserts) — Dhis2ManagementPage
// tracks what it has mapped THIS SESSION locally rather than inventing a
// backend read it doesn't have.
export const dhis2API = {
  getOrgUnitLevels: (id) => api.get(`/api/dhis2/${id}/metadata/org-unit-levels`),
  bootstrap: (id, body) => api.post(`/api/dhis2/${id}/bootstrap`, body),
  listOrgUnits: (id, params = {}) => api.get(`/api/dhis2/${id}/org-units`, { params }),
  setOrgUnitOverride: (id, orgUnitId, body) => api.put(`/api/dhis2/${id}/org-units/${encodeURIComponent(orgUnitId)}`, body),
  confirmOrgUnit: (id, orgUnitId) => api.post(`/api/dhis2/${id}/org-units/${encodeURIComponent(orgUnitId)}/confirm`),
  setPullDataElementMapping: (id, body) => api.post(`/api/dhis2/${id}/data-elements`, body),
  setPushDataElementMapping: (id, body) => api.post(`/api/dhis2/${id}/data-elements/push`, body),
  // Pull has NO dry-run — routes/dhis2.js's triggerPull always writes the
  // clean subset (STOPs come back in the report, not thrown). Push's dryRun
  // rides straight through to DHIS2's own dryRun query param.
  triggerPull: (id, body) => api.post(`/api/dhis2/${id}/pull`, body),
  triggerPush: (id, body) => api.post(`/api/dhis2/${id}/push`, body),
}

// Dynamic Schema API endpoints
export const schemaAPI = {
  // routes/tables.js
  // Trailing slash is load-bearing: GET /api/tables (no slash) hits the
  // router's exact mount root and gets a 301 from the edge whose Location
  // downgrades to http:// — the browser then blocks the follow-up as
  // cross-origin/mixed-content. GET /api/tables/ reaches the route directly
  // (verified: 401 vs 301 against prod). No other schemaAPI call hits a bare
  // mount root, so this is the only endpoint that needs it.
  // Also the "Datasets" entity source for the ⌘K global search
  // (hooks/useGlobalSearch.js's useGlobalSearchEntities) — called with
  // { limit: 100 } (the route's max page size); a schema past 100 tables
  // would need real server-side search wired in (the route already
  // supports ?search=, unused here yet — client-side filtering is enough
  // at today's table counts).
  getTables: (params = {}) =>
    api.get('/api/tables/', { params }),

  createTable: (tableData) =>
    api.post('/api/tables/create', tableData),

  getTable: (tableName) =>
    api.get(`/api/tables/${tableName}`),

  // Single ALTER op: { action: 'add_column'|'drop_column'|'modify_column'|
  // 'add_index'|'drop_index', columnName, columnType, indexName, indexColumns }.
  modifyTable: (tableName, modification) =>
    api.put(`/api/tables/${tableName}/modify`, modification),

  // Backend requires { confirm: 'DELETE' } in the body to guard against
  // accidental drops — the page's own confirm() dialog is a separate check.
  deleteTable: (tableName) =>
    api.delete(`/api/tables/${tableName}`, { data: { confirm: 'DELETE' } }),

  // routes/schema.js — table generation + per-table schema record
  generateTableFromModel: (modelId, options = {}) =>
    api.post('/api/schema/generate/from-model', { modelId, options }),

  getSchemas: (params = {}) =>
    api.get('/api/schema/list', { params }),

  getSchema: (schemaId) =>
    api.get(`/api/schema/${schemaId}`),

  // Real tables + FK edges across every application schema, for the Schema
  // page's Relationships/ERD panel. See routes/schema.js GET /relationships —
  // must be registered before the GET /:id route since ':id' requires a UUID.
  getRelationships: () =>
    api.get('/api/schema/relationships'),

  // routes/versioning.js — real versioning is keyed by tableId, not a bare
  // version id. tableId is the generated_tables.id UUID.
  getSchemaVersions: (tableId, params = {}) =>
    api.get(`/api/versioning/table/${tableId}`, { params }),

  createSchemaVersion: (versionData) =>
    api.post('/api/versioning/create', versionData),

  getSchemaVersion: (tableId, versionNumber) =>
    api.get(`/api/versioning/table/${tableId}/version/${versionNumber}`),

  compareSchemaVersions: (tableId, fromVersion, toVersion) =>
    api.get('/api/versioning/compare', { params: { tableId, fromVersion, toVersion } }),

  // Backend has no from/to compare for compatibility — it's a report across
  // all of a table's versions.
  checkBackwardCompatibility: (tableId) =>
    api.get(`/api/versioning/compatibility/${tableId}`),

  // routes/migrations.js
  getMigrations: (params = {}) =>
    api.get('/api/migrations/status', { params }),

  createMigration: (migrationData) =>
    api.post('/api/migrations/create', migrationData),

  getMigration: (migrationId) =>
    api.get(`/api/migrations/${migrationId}`),

  executeMigration: (migrationId, options = {}) =>
    api.post('/api/migrations/run', { migrationId, ...options }),

  rollbackMigration: (migrationId, options = {}) =>
    api.post('/api/migrations/rollback', { migrationId, ...options }),

  // No standalone validate endpoint — /run's dryRun mode is the real
  // "check the SQL without applying it" path.
  validateMigration: (migrationData) =>
    api.post('/api/migrations/run', { ...migrationData, dryRun: true }),
}

// Health check endpoints
export const healthAPI = {
  checkAuth: () =>
    api.get('/api/auth/health'),

  checkModels: () =>
    api.get('/api/models/health'),

  checkSchema: () =>
    api.get('/api/schema/health'),

  checkPredictions: () =>
    api.get('/api/predictions/health'),

  getSystemStatus: () =>
    Promise.all([
      healthAPI.checkAuth().catch(()        => ({ status: 'down', service: 'auth' })),
      healthAPI.checkModels().catch(()      => ({ status: 'down', service: 'models' })),
      healthAPI.checkSchema().catch(()      => ({ status: 'down', service: 'schema' })),
      healthAPI.checkPredictions().catch(() => ({ status: 'down', service: 'predictions' })),
    ]).then(results => ({
      auth:        results[0],
      models:      results[1],
      schema:      results[2],
      predictions: results[3],
      timestamp:   new Date().toISOString()
    })),
}

// Data Ingestion API endpoints.
// Same-origin by default (see note on `api` above). The /api/data/* routes are
// mounted on the dynamic-schema service (port 3003); nginx / Vite proxy handles
// the port routing. VITE_DATA_API_URL is only needed for cross-origin API hosts.
const dataClient = axios.create({
  baseURL: import.meta.env.VITE_DATA_API_URL ?? '',
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
})

dataClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('iewrs_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

dataClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const { response } = error
    if (response?.status === 401) {
      // Same silent-URL rules as the main client above.
      const url = error.config?.url || ''
      const hasToken = localStorage.getItem('iewrs_token')
      if (hasToken && !isSilent401(url)) {
        handleSessionExpired()
      }
    } else if (response?.status === 403) {
      toast.error('Access denied. Insufficient permissions.', { id: 'forbidden' })
    }
    return Promise.reject(error)
  }
)

export const dataAPI = {
  // Import already-parsed CSV (frontend parses with csvPreviewParser, sends JSON).
  // Body: { metricType, unit, sourceName, headers: [...], rows: [[...], ...] }
  importParsed: (payload) =>
    dataClient.post('/api/data/import', payload, {
      timeout: 300000, // 5 min for large imports
    }),

  // Zero-write shape detection for the bundle-import wizard (contract C2).
  // Same client-parsed shape as importParsed minus metricType — the server
  // returns its interpretation (shape, geoLevel, columnMapping, counts,
  // requiresConfirm) for the operator to confirm before any commit. Short
  // timeout: this is preview-only, it must never run a long import.
  detect: (payload) =>
    dataClient.post('/api/data/import/detect', payload, { timeout: 30000 }),

  // Register (promote) a new metric into the registry so a bundle file with
  // no existing metric can be imported under it. Body: { metricType, label,
  // unit, category, aggregation?, ... } — metricType/label/unit/category are
  // required server-side (routes/registry.js). Gated by requireSchemaPermission;
  // a 403 is surfaced by the caller as a "request this metric" fallback, not
  // a dead end.
  registerMetric: (body) =>
    dataClient.post('/api/data/registry/metrics', body),

  // Legacy: multipart upload (kept for compat; prefer importParsed)
  uploadCSV: (formData, onProgress) =>
    dataClient.post('/api/data/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }),

  getImportJob: (jobId) => dataClient.get(`/api/data/jobs/${jobId}`),

  getImportJobs: (params) => dataClient.get('/api/data/jobs', { params }),

  getMetrics: () => dataClient.get('/api/data/metrics'),

  // Predictions surfaced from notebook runs so Visual Creator can chart
  // predicted_cases vs date alongside raw measurements. Each notebook run
  // becomes a selectable "prediction metric" in the UI.
  getPredictionRuns: () => dataClient.get('/api/data/predictions/runs'),
  queryPredictions:  (params) => dataClient.get('/api/data/predictions/series', { params }),

  // Compact recent-history endpoint for hover sparklines.
  // params: { metricType, sectorId?, sectorName?, district?, months? }
  getSectorSummary: (params) => dataClient.get('/api/data/sector-summary', { params }),

  // Reference data (416 sectors' area in km^2) for the density transform —
  // see frontend/src/data/sectorAreas.js, which caches this for the session.
  getSectorAreas: () => dataClient.get('/api/data/sector-areas'),

  getMetricCoverage: (metricType) =>
    dataClient.get(`/api/data/metrics/${metricType}/coverage`),

  // Observed monthly incidence + population per sector, for the disease map's
  // Incidence <-> Cases toggle. params: { metricType: '<disease>_incidence'
  //   (flu is 'flu_incidence_revised'), month: 'YYYY-MM' }. Response:
  //   { month, metricType, disease, data: [
  //   { sector_id, location_code, district_code, incidence_per_1000, population,
  //     cases } ] } — incidence_per_1000 is native (stored), cases is derived
  //   server-side (incidence * population / 1000). Each of the three is
  //   independently nullable (no measurement that month, no population row that
  //   month); never coerce a null to 0 client-side.
  getIncidence: (params) => dataClient.get('/api/data/incidence', { params }),

  queryMeasurements: (params) =>
    dataClient.get('/api/data/query', { params }),

  // Option B — server-side aggregation. Returns pre-grouped rows instead
  // of every raw measurement.
  //   params.groupBy: 'sector' | 'sector,season' | 'sector,year'
  //                 | 'sector,month' | 'month'
  // Same metric/date/district filters as queryMeasurements. Read-only;
  // imported rows are never modified.
  aggregateMeasurements: (params) =>
    dataClient.get('/api/data/aggregate', { params }),

  exportData: (params) =>
    dataClient.get('/api/data/export', { params, responseType: 'blob' }),

  getSources: () => dataClient.get('/api/data/sources'),

  createSource: (data) => dataClient.post('/api/data/sources', data),

  // Metric registry (data/metricCatalog.js is the normalizing client for
  // these two — see IMPORT_SPEC.md §2.2). registry UNION observed coverage;
  // a metric present in `measurements` but never registered still appears,
  // with unregistered: true.
  getRegistryMetrics: () => dataClient.get('/api/data/registry/metrics'),

  // disease_key + csv_aliases catalogue, scoped to is_active metrics.
  getRegistryAliases: () => dataClient.get('/api/data/registry/aliases'),

  // Download a canonical CSV template that matches the malaria/flu/climate
  // "N_SectorName" convention. Returns a Blob; caller triggers a browser
  // download with a synthetic <a download> click.
  downloadCsvTemplate: ({ shape = 'sector-wide', withExamples = false } = {}) =>
    dataClient.get('/api/data/csv-template', {
      params: { shape, withExamples: withExamples ? 'true' : 'false' },
      responseType: 'blob',
    }),
}

// Landing page and public API endpoints
export const landingAPI = {
  // On a network failure return nulls, never invented figures — the landing
  // page's asMetric() renders a dash for a null, so a down endpoint shows
  // "—" rather than fabricated marketing numbers. The real counts come from
  // GET /api/public/metrics (live DB COUNTs, cached server-side).
  getSystemMetrics: () =>
    api.get('/api/public/metrics').catch(() => ({
      models: null,
      users: null,
      alerts: null,
      dataPoints: null
    })),

  getPartnerInfo: () =>
    api.get('/api/public/partners').catch(() => []),

  getTeamInfo: () =>
    api.get('/api/public/team').catch(() => []),

  getSystemOverview: () =>
    api.get('/api/public/overview').catch(() => ({
      description: 'Integrated Early Warning and Response System for climate-health monitoring',
      features: [],
      targetAudience: []
    })),

  submitContactForm: (formData) =>
    api.post('/api/public/contact', formData),

  requestSystemAccess: (requestData) =>
    api.post('/api/public/access-request', requestData),
}

// Marketing-site image + ordering management. GET is public (the landing
// page renders for unauthenticated visitors); POST is gated by the
// authenticated /app/settings/content/images page in the UI (moved from
// /app/settings/images — Task #8, settings consolidation). The backend
// itself currently doesn't auth-check POST — that's a follow-up, and
// still true after the consolidation (flagged, not fixed here).
export const imagesAPI = {
  get: () => api.get('/api/public/images'),

  // Patch shape: any combination of {teamImages, partnerLogos, sectionImages,
  // teamRoster, partnerRoster, teamOrder, partnerOrder}. teamOrder/partnerOrder
  // are full arrays of IDs in display order. A key mapped to `null` inside
  // teamImages/partnerLogos/sectionImages is a reset — it clears that id's
  // stored override so the public page falls back to its hardcoded default.
  update: (patch) => api.post('/api/public/images', patch),
}

// Editable landing-page content (hero/objectives/diseases/etc.).
// Lives at data/landing-content.json server-side. GET returns merged
// defaults + overrides; POST stores the full content object as overrides.
export const landingContentAPI = {
  get: () => api.get('/api/public/landing-content'),
  update: (content) => api.post('/api/public/landing-content', content),
}

// App branding (in-app logo + browser favicon). GET is public — the login
// page logo and the favicon render before anyone authenticates, so
// BrandingContext loads this above AuthProvider. POST is super_admin-only
// server-side (routes/public.js). Patch shape: any subset of { logo,
// favicon } as base64 image data URLs; a field set to null is a reset
// (clears the override → bundled default). SVG is rejected server-side.
export const brandingAPI = {
  get: () => api.get('/api/public/branding'),
  update: (patch) => api.post('/api/public/branding', patch),
}

// RBAC admin endpoints. Mirrors the routes in services/auth/routes/admin.js.
// Every call is gated on either `manage_users` or `approve_signups`
// permission server-side, so a non-admin caller gets 403 even if they
// reach the URL.
export const adminAPI = {
  // Users
  listUsers:        ()                  => api.get('/api/auth/admin/users'),
  getUser:          (userId)            => api.get(`/api/auth/admin/users/${userId}`),
  createUser:       (body)              => api.post('/api/auth/admin/users', body),
  updateUser:       (userId, body)      => api.put(`/api/auth/admin/users/${userId}`, body),
  setUserRole:      (userId, role)      => api.put(`/api/auth/admin/users/${userId}/role`, { role }),
  setUserOverrides: (userId, overrides) => api.put(`/api/auth/admin/users/${userId}/permissions`, { overrides }),
  deactivateUser:   (userId)            => api.post(`/api/auth/admin/users/${userId}/deactivate`),
  reactivateUser:   (userId)            => api.post(`/api/auth/admin/users/${userId}/reactivate`),
  // Resend the 24h password-setup link when a user's original welcome
  // email expired before they clicked it. Refuses if already verified
  // (use forgot-password flow instead) or deactivated.
  resendSetupLink:  (userId)            => api.post(`/api/auth/admin/users/${userId}/resend-setup-link`),

  // Access requests (self-signup approval queue)
  listAccessRequests:   (status = 'pending') =>
    api.get('/api/auth/admin/access-requests', { params: { status } }),
  approveAccessRequest: (id, body = {})      => api.post(`/api/auth/admin/access-requests/${id}/approve`, body),
  rejectAccessRequest:  (id, notes)          => api.post(`/api/auth/admin/access-requests/${id}/reject`, { notes }),

  // Catalog (read-only)
  listRoles:           ()         => api.get('/api/auth/admin/roles'),
  listPermissions:     ()         => api.get('/api/auth/admin/permissions'),
  getRolePermissions:  (roleName) => api.get(`/api/auth/admin/roles/${roleName}/permissions`),

  // Public team admin
  updateUserProfile:   (userId, patch) => api.put(`/api/auth/admin/users/${userId}/profile`, patch),
  setUserLanding:      (userId, patch) => api.put(`/api/auth/admin/users/${userId}/landing`, patch),
  requestUserEdit:     (userId, note)  => api.post(`/api/auth/admin/users/${userId}/request-edit`, { note }),
  listLandingUsers:    ()              => api.get('/api/auth/admin/users/landing'),
}

// RBAC dynamic config console (Slice 1 backend — services/auth/routes/rbac.js,
// mounted at /api/auth/rbac). Every route is gated on `manage_users`
// server-side; the typed error codes it can return (CANNOT_GRANT_BEYOND_SELF,
// CANNOT_DEMOTE_ADMIN, LAST_SUPER_ADMIN, SELF_LOCKOUT, SUPER_ADMIN_IMMUTABLE,
// SYSTEM_ROLE_PROTECTED, ROLE_IN_USE, ...) are humanized by
// pages/admin/rolesConsoleLogic.mjs — never surface err.response.data.code
// to the UI directly.
export const rbacAPI = {
  // Whole graph in one call: roles (+is_system/member_count), permissions,
  // permissions grouped by resource ("modules"), and the role_id ->
  // [permission_id] matrix. Drives the Roles & Permissions console.
  getCatalog: () => api.get('/api/auth/rbac/catalog'),

  // Transactional full-set replace — send the role's COMPLETE resulting
  // permission-id array, not a delta; the server diffs against its own
  // current state. Rejected outright (403 SUPER_ADMIN_IMMUTABLE) for the
  // super_admin role.
  setRolePermissions: (roleId, permissionIds) =>
    api.put(`/api/auth/rbac/roles/${roleId}/permissions`, { permissionIds }),

  // clone_from_role_id is optional — omit to start with zero permissions.
  createRole: (body) => api.post('/api/auth/rbac/roles', body),

  // { role_name?, description? } — role_name is rejected (403
  // SYSTEM_ROLE_PROTECTED) for any system role; description always allowed.
  updateRole: (roleId, body) => api.patch(`/api/auth/rbac/roles/${roleId}`, body),

  // Refused (403 SYSTEM_ROLE_PROTECTED) for system roles, or (409
  // ROLE_IN_USE, with `member_count`) while any user still holds it.
  deleteRole: (roleId) => api.delete(`/api/auth/rbac/roles/${roleId}`),

  // Roles + per-user grant/deny overrides + resolved effective permission
  // list for one user.
  getUserAccess: (userId) => api.get(`/api/auth/rbac/users/${userId}/access`),

  // { roleIds: [uuid], overrides?: [{ permission_id, is_granted, expires_at? }] }
  // — full replace of both.
  setUserAccess: (userId, body) => api.put(`/api/auth/rbac/users/${userId}/access`, body),
}

// Public team listing — used by the public landing page Team section.
export const teamAPI = {
  getPublicTeam: () => api.get('/api/public/team'),
}

export const settingsAPI = {
  runtime: () => api.get('/api/settings/runtime'),
  save: (key, value, note = '') => api.put(`/api/settings/${key}`, { value, note }),
  saveBatch: (map, localization, note = '') => api.put('/api/settings/batch', { map, localization, note }),
  history: (key) => api.get(`/api/settings/history/${key}`),
  rollback: (key, revision) => api.post(`/api/settings/${key}/rollback/${revision}`),
  uploadGeoJSON: (document) => api.post('/api/settings/assets/geojson', document),
  // `arrayBuffer` is the raw .zip bytes (File#arrayBuffer()) — sent as the
  // literal request body, not multipart/form-data, matching the backend's
  // express.raw() route (POST /api/settings/assets/shapefile). The server
  // unzips, parses the shapefile, reprojects to WGS84 and validates it
  // through the exact same pipeline as uploadGeoJSON above.
  uploadShapefile: (arrayBuffer) => api.post('/api/settings/assets/shapefile', arrayBuffer, { headers: { 'Content-Type': 'application/zip' } }),
}

export default api
